import React, { useEffect, useState, useCallback } from 'react';
import { arrayOf, oneOfType, array, bool, func } from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { isUserAdmin } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import {
  getQueryParamNames,
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  moveSecondFilterToEnd,
} from '../../util/search';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  H2,
  Page,
  AvatarLarge,
  NamedRedirect,
  NamedLink,
  SecondaryButton,
  ExpandableBio,
  ReviewRating,
  Modal,
} from '../../components';

import FilterComponent from '../SearchPage/FilterComponent';
import SearchFiltersMobile from '../SearchPage/SearchFiltersMobile/SearchFiltersMobile';
import SortBy from '../SearchPage/SortBy/SortBy';
import {
  groupListingFieldConfigs,
  initialValues,
  pickListingFieldFilters,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
} from '../SearchPage/SearchPage.shared';
import { getUserCountry, getLocationString, useHandleChangedValueFn } from './ProfilePage.shared';
import { sendReview, contactSeller } from './ProfilePage.duck';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import TopbarNavigation from '../TopbarContainer/Topbar/TopbarNavigation/TopbarNavigation';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import ProfileNav, { TAB_REVIEWS } from './ProfileNav/ProfileNav';
import InquiryForm from './InquiryForm/InquiryForm';
import css from './ProfilePage.module.css';
import IconSimpleMail from '../../components/IconSimpleMail/IconSimpleMail';

// Search is in modal on mobile layout
const MODAL_BREAKPOINT = 768;

// Approximated collapsed size so that there are ~four lines of text
// in the desktop layout in the author section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 620;

export const UserContent = props => {
  const { pathParams, user, isCurrentUser, displayName, bio, userReviews, listings, onOpenInquiryModal } = props;
  const { publicData, metadata } = user.attributes.profile;

  // Bio
  const hasBio = !!bio;

  // Location
  const userCountry = getUserCountry(publicData?.country);
  const userCity = publicData?.city;
  const userLocation = getLocationString(userCountry, userCity);

    // Contact seller button
  const canContactSeller = !isCurrentUser && listings && listings.length >= 1;

  const userName = user.attributes.profile.displayName

  // Review count
  const reviewCount = userReviews?.length || 0;

  // Average rating
  const averageRating = metadata?.averageRating;
  const averageRatingAsNumber = averageRating ? parseFloat(averageRating) : 0;
  useEffect(() => {
    console.log("user.attributes.profile",user.attributes.profile)
  }, [user])

  if (pathParams.tab === TAB_REVIEWS) {
    return (
      <div className={css.userContent}>
        <div className={css.userContentWrapper}>
          <H2 className={classNames(css.heading, css.reviewsHeading)}>{displayName}</H2>
        </div>
      </div>
    );
  }

  return (
    <div className={css.userContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <div className={css.userContentWrapper}>
        <H2 className={css.heading}>{displayName}</H2>
        {userLocation ? <p className={css.location}>{userLocation}</p> : null}
        <NamedLink
          className={css.topReviews}
          name="ProfilePage"
          params={{
            id: user.id.uuid,
            tab: TAB_REVIEWS,
          }}
        >
          <ReviewRating reviewStarClassName={css.topReviewStar} rating={averageRatingAsNumber} />
          <p className={css.topReviewsMessage}>
            <FormattedMessage
              id="ProfilePage.topReviews"
              values={{
                count: reviewCount,
              }}
            />
          </p>
        </NamedLink>
        {hasBio ? (
          <ExpandableBio className={css.bio} bio={bio} bioLength={BIO_COLLAPSED_LENGTH} />
        ) : null}
        {canContactSeller ? (
            <button
              type='button'
              className={css.contactSellerButton}
              onClick={onOpenInquiryModal}
            >
              <IconSimpleMail />
              <p className={css.contactSellerLink}>Contact {userName}</p>
            </button>
          ) : null}
        {isCurrentUser ? (
          <NamedLink className={css.editProfileButton} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfile" />
          </NamedLink>
        ) : null}
      </div>
    </div>
  );
};

const MainContent = props => {
  return (
    <div className={css.mainContent}>
      <ProfileNav {...props} />
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();

  const [mounted, setMounted] = useState(false);
  const [isFiltersOpenOnMobile, setIsFiltersOpenOnMobile] = useState(false);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    intl,
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    showUserError,
    user,
    userReviews,
    onSendReview,
    onManageDisableScrolling,
    contactSellerInProgress,
    contactSellerError,
    onContactSeller,
    listngs,
    // ...rest
    ...rest
  } = props;

  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const listingsProps = {
    searchListingsInProgress: props.searchListingsInProgress,
    searchListingsError: props.searchListingsError,
    listings: props.listings,
  };

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, metadata } = profileUser?.attributes?.profile || {};
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showUserError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const isDataLoaded = isPreview
    ? currentUser != null || showUserError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || showUserError != null
    : user != null || showUserError != null;

  const { listingFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const marketplaceCurrency = config.currency;
  const categoryConfiguration = config.categoryConfiguration;
  const listingCategories = categoryConfiguration.categories;

  const listingFieldsConfig = pickListingFieldFilters({
    listingFields,
    locationSearch: location.search,
    categoryConfiguration,
  });
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
  };

  // Page transition might initially use values from previous search
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  const { searchParamsAreInSync, urlQueryParams } = searchParamsPicker(
    location.search,
    searchParams,
    filterConfigs,
    sortConfig,
    isOriginInUse(config)
  );

  const listingsAreLoaded = !listingsProps.searchListingsInProgress && searchParamsAreInSync;
  const totalItems = listingsProps.listings.length;
  const validQueryParams = urlQueryParams;

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const builtInPrimaryFilters = defaultFiltersConfig.filter(f => ['categoryLevel'].includes(f.key));
  const builtInFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
    : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );
  const availableFilters = [
    ...builtInPrimaryFilters,
    ...customPrimaryFilters,
    ...builtInFilters,
    ...customSecondaryFilters,
  ];
  const sortedAvailableFilters = moveSecondFilterToEnd(availableFilters);

  const selectedFilters = validQueryParams;
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;
  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    filterConfigs
  );
  const propsForValidUrlQueryParams = {
    location,
    config,
  };

  const [currentQueryParams, handleChangedValue] = useHandleChangedValueFn(
    config,
    filterConfigs,
    sortConfig,
    routeConfiguration,
    pathParams,
    props.marketplaceListings
  );

  const handleSortByFn = (urlParam, values) => {
    const urlQueryParams = validUrlQueryParamsFromProps(propsForValidUrlQueryParams);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, pathParams, queryParams)
    );
  };

  const sortBy = mode => {
    return sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={handleSortByFn}
        showAsPopup
        mode={mode}
      />
    ) : null;
  };

  const getHandleChangedValueFn = useCallback(
    useHistoryPush => updatedURLParams => handleChangedValue(updatedURLParams, useHistoryPush),
    [handleChangedValue]
  );

  const resetAllFiltersFn = e => {
    const urlQueryParams = validUrlQueryParamsFromProps(propsForValidUrlQueryParams);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, pathParams, queryParams)
    );
  };

  const searchFiltersDesktopComponent = (
    <div className={css.filtersWrapper}>
      <div className={css.filters}>
        {sortedAvailableFilters.map(filterConfig => {
          const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
            filterConfig.key
          }`;
          return (
            <FilterComponent
              key={key}
              idPrefix="SearchFiltersDesktop"
              className={css.filter}
              config={filterConfig}
              currentPage="ProfilePage"
              listingCategories={listingCategories}
              marketplaceCurrency={marketplaceCurrency}
              urlQueryParams={currentQueryParams}
              initialValues={initialValues(propsForValidUrlQueryParams, currentQueryParams)}
              getHandleChangedValueFn={getHandleChangedValueFn}
              intl={intl}
              liveEdit
              showAsPopup={false}
              isDesktop
            />
          );
        })}
      </div>
      <SecondaryButton className={css.resetAllButton} onClick={resetAllFiltersFn}>
        <FormattedMessage id={'ProfilePage.resetAllFilters'} />
      </SecondaryButton>
    </div>
  );

  const searchFiltersMobileComponent = (
    <SearchFiltersMobile
      urlQueryParams={validQueryParams}
      sortByComponent={sortBy('mobile')}
      resultsAreLoaded={listingsAreLoaded}
      resultsCount={totalItems}
      searchInProgress={listingsProps.searchListingsInProgress}
      searchListingsError={listingsProps.searchListingsError}
      showAsModalMaxWidth={MODAL_BREAKPOINT}
      onManageDisableScrolling={onManageDisableScrolling}
      onOpenModal={() => setIsFiltersOpenOnMobile(true)}
      onCloseModal={() => setIsFiltersOpenOnMobile(false)}
      resetAll={resetAllFiltersFn}
      selectedFiltersCount={selectedFiltersCountForMobile}
      isMapVariant={false}
      cancelPageName="ProfilePage"
      cancelPageParams={pathParams}
    >
      {sortedAvailableFilters.map(filterConfig => {
        const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
        return (
          <FilterComponent
            key={key}
            idPrefix="SearchFiltersMobile"
            config={filterConfig}
            currentPage="ProfilePage"
            listingCategories={listingCategories}
            marketplaceCurrency={marketplaceCurrency}
            urlQueryParams={currentQueryParams}
            initialValues={initialValues(propsForValidUrlQueryParams, currentQueryParams)}
            getHandleChangedValueFn={getHandleChangedValueFn}
            intl={intl}
            liveEdit
            showAsPopup={false}
          />
        );
      })}
    </SearchFiltersMobile>
  );

  const searchFiltersComponent = {
    desktop: searchFiltersDesktopComponent,
    mobile: searchFiltersMobileComponent,
  };

  const topbarSearchFormPlaceholder = intl.formatMessage(
    {
      id: 'ProfilePage.topbarSearchFormPlaceholder',
    },
    {
      displayName,
    }
  );

  // Set topbar class based on if a modal is open in
  // a child component
  const topbarClasses = isFiltersOpenOnMobile
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(showUserError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(showUserError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(showUserError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }
  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <TopbarContainer
        rootClassName={topbarClasses}
        currentPage="ProfilePage"
        currentSearchParams={validQueryParams}
        topbarSearchFormPlaceholder={topbarSearchFormPlaceholder}
      />
      <TopbarNavigation />
      <div className={css.content}>
        <UserContent
          pathParams={pathParams}
          user={profileUser}
          isCurrentUser={isCurrentUser}
          bio={bio}
          displayName={displayName}
          userReviews={userReviews}
          listings={listingsProps.listings}
          onOpenInquiryModal={() => setInquiryModalOpen(true)}
        />
        <MainContent
          pathParams={pathParams}
          currentUser={currentUser}
          isCurrentUser={isCurrentUser}
          searchFiltersComponent={searchFiltersComponent}
          sortByComponent={sortBy('desktop')}
          userReviews={userReviews}
          onSendReview={review => onSendReview(user.id.uuid, review)}
          {...rest}
        />
      </div>
      <Modal
        id="ProfilePage.inquiry"
        containerClassName={css.inquiryModalContainer}
        isOpen={inquiryModalOpen}
        onClose={() => setInquiryModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          key={inquiryModalOpen}
          className={css.inquiryForm}
          authorDisplayName={displayName}
          sendInquiryError={contactSellerError}
          onSubmit={values => {
            const listingId = listingsProps.listings[0].id.uuid;
            const message = values.message.trim();
            onContactSeller(listingId, message).then(() => {
              setInquiryModalOpen(false);
            });
          }}
          inProgress={contactSellerInProgress}
        />
      </Modal>
      <FooterContainer />
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  showUserInProgress: false,
  showUserError: null,
  searchListingsInProgress: false,
  searchListingsError: null,
  sendReviewInProgress: false,
  marketplaceListings: [],
  sendReviewError: null,
  reviewSubmitted: false,
  userReviews: [],
  fetchUserReviewsInProgress: false,
  fetchUserReviewsError: null,
  contactSellerInProgress: false,
  contactSellerError: null,
  logoutInProgress: false,
  onSendReview: null,
  onManageDisableScrolling: null,
  onContactSeller: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  useCurrentUser: bool.isRequired,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  showUserInProgress: bool.isRequired,
  showUserError: propTypes.error,
  searchListingsInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  listings: arrayOf(oneOfType([propTypes.listing, propTypes.ownListing])).isRequired,
  marketplaceListings: array.isRequired,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  reviewSubmitted: bool.isRequired,
  userReviews: arrayOf(propTypes.userReview).isRequired,
  fetchUserReviewsInProgress: bool.isRequired,
  fetchUserReviewsError: propTypes.error,
  contactSellerInProgress: bool.isRequired,
  contactSellerError: propTypes.error,
  logoutInProgress: bool.isRequired,
  onSendReview: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onContactSeller: func.isRequired,
};

const EnhancedProfilePage = props => {
  const intl = useIntl();
  const { user, showUserInProgress, logoutInProgress, scrollingDisabled, ...restOfProps } = props;

  if (showUserInProgress) {
    // Render a loading page while user data is being fetched
    const schemaTitle = intl.formatMessage({ id: 'ProfilePage.loadingSchemaTitle' });

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ProfilePage',
          name: schemaTitle,
        }}
      >
        <TopbarContainer />
        <TopbarNavigation />
        <div className={css.content}>
          <p className={css.loadingMessage}>
            <FormattedMessage id="ProfilePage.loadingUser" />
          </p>
        </div>
        <FooterContainer />
      </Page>
    );
  }

  if (!logoutInProgress && !isUserAdmin(user)) {
    // Redirect to NotFoundPage if the user is not an admin
    return <NamedRedirect name="NotFoundPage" />;
  }

  // Render the profile page for admin users
  return (
    <ProfilePageComponent
      intl={intl}
      user={user}
      scrollingDisabled={scrollingDisabled}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { logoutInProgress } = state.auth;
  const {
    userId,
    showUserInProgress,
    showUserError,
    searchListingsInProgress,
    searchListingsError,
    userListingRefs,
    marketplaceListings,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    userReviewIds,
    fetchUserReviewsInProgress,
    fetchUserReviewsError,
    contactSellerInProgress,
    contactSellerError,
  } = state.ProfilePage;
  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const userReviews = getMarketplaceEntities(state, userReviewIds);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    showUserInProgress,
    showUserError,
    searchListingsInProgress,
    searchListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    marketplaceListings,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    userReviews,
    fetchUserReviewsInProgress,
    fetchUserReviewsError,
    contactSellerInProgress,
    contactSellerError,
    logoutInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onSendReview: (userId, review) => dispatch(sendReview(userId, review)),
  onManageDisableScrolling: (componentId, disableScrolling) => {
    dispatch(manageDisableScrolling(componentId, disableScrolling));
  },
  onContactSeller: (listingId, message) => dispatch(contactSeller(listingId, message)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedProfilePage);

export default ProfilePage;
